import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { ScanFace, Hash, Palette, Shield, Zap, Globe } from "lucide-react";
import bibDetectionImage from "@/assets/bib-detection-demo.jpg";
import colorDetectionImage from "@/assets/color-detection-demo.jpg";
import facialRecognitionImage from "@/assets/feature-facial-recognition.jpg";
import secureCheckoutImage from "@/assets/feature-secure-checkout.jpg";
import lightningFastImage from "@/assets/feature-lightning-fast.jpg";
import customStorefrontImage from "@/assets/feature-custom-storefront.jpg";

const FeaturesSection = () => {
  const { t } = useTranslation();

  const features = [
    { icon: Hash, title: t("features.bibDetection.title"), description: t("features.bibDetection.description"), color: "bg-hawk-orange", image: bibDetectionImage },
    { icon: ScanFace, title: t("features.facialRecognition.title"), description: t("features.facialRecognition.description"), color: "bg-blue-500", image: facialRecognitionImage },
    { icon: Palette, title: t("features.colourDetection.title"), description: t("features.colourDetection.description"), color: "bg-emerald-500", image: colorDetectionImage },
    { icon: Shield, title: t("features.secureCheckout.title"), description: t("features.secureCheckout.description"), color: "bg-violet-500", image: secureCheckoutImage },
    { icon: Zap, title: t("features.lightningFast.title"), description: t("features.lightningFast.description"), color: "bg-amber-500", image: lightningFastImage },
    { icon: Globe, title: t("features.customBranding.title"), description: t("features.customBranding.description"), color: "bg-pink-500", image: customStorefrontImage },
  ];

  return (
    <section id="features" className="py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-hawk-orange/10 text-hawk-orange text-sm font-semibold mb-4">{t("features.badge")}</span>
          <h2 className="text-4xl md:text-5xl font-display font-bold mb-6">{t("features.heading")}</h2>
          <p className="text-lg text-muted-foreground">{t("features.subtitle")}</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {features.map((feature, index) => (
            <Card key={feature.title} variant="feature" className="group overflow-hidden" style={{ animationDelay: `${index * 0.1}s` }}>
              <div className="relative h-40 overflow-hidden">
                <img src={feature.image} alt={feature.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-card via-card/50 to-transparent" />
                <div className={`absolute bottom-3 left-6 w-12 h-12 rounded-xl ${feature.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  <feature.icon className="text-white" size={24} />
                </div>
              </div>
              <CardContent className="p-6 pt-4">
                <h3 className="text-xl font-display font-bold mb-3">{feature.title}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">{feature.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
